import React from 'react'
import { useTable, EditButton, DeleteButton } from '@refinedev/antd'
import { Space } from 'antd'
import type { Tables } from '@muaa/shared'
import { brand } from '../../theme/brand'
import { ListPage, SectionCard, EmptyHint, type Stat } from '../../theme/listkit'
import { Ticket } from '../../components/icons'

type Meal = Tables<'meals'>

/** A single meal: bordered card, Cinzel name, inline Edit/Delete (PK is `name`). */
function MealCard({ name }: { name: string }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 10,
        padding: '14px 16px',
        borderRadius: 12,
        background: '#fff',
        border: '1px solid ' + brand.cardLine,
        borderLeft: '3px solid ' + brand.gold,
      }}
    >
      <span
        style={{
          fontFamily: brand.fDisplay,
          fontWeight: 700,
          fontSize: 14,
          color: brand.ink,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {name}
      </span>
      <Space size={2} style={{ flexShrink: 0 }}>
        <EditButton hideText size="small" recordItemId={name} />
        <DeleteButton hideText size="small" recordItemId={name} />
      </Space>
    </div>
  )
}

export function MealList() {
  // useTable establishes the `meals` resource context and loads the rows;
  // we render our own chip grid instead of a table. Meals are a small,
  // domain-bounded set — cap the page at 200 (explicit bound instead of
  // mode:'off') so the whole menu renders in one chip grid.
  const { tableProps } = useTable<Meal>({
    syncWithLocation: true,
    pagination: { pageSize: 200 },
  })
  const meals = (tableProps.dataSource ?? []) as Meal[]

  const stats: Stat[] = [{ label: 'Total Meals', value: meals.length.toLocaleString(), icon: Ticket, tone: 'gold' }]

  return (
    <ListPage
      intro={{
        eyebrow: 'Events · Meal Options',
        description:
          'The meal options members can select when they RSVP to a Homecoming event — banquet plates, brunch seatings, and dietary choices. Keep the menu current before registration opens.',
      }}
      stats={stats}
      statColumns={3}
    >
      <SectionCard title="Meal Options" sub="Selectable at event RSVP">
        {meals.length === 0 ? (
          <EmptyHint>No meals yet.</EmptyHint>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: 12,
            }}
          >
            {meals.map((meal) => (
              <MealCard key={String(meal.name)} name={meal.name} />
            ))}
          </div>
        )}
      </SectionCard>
    </ListPage>
  )
}
